import React, { useState, useRef } from 'react';
import { X } from 'lucide-react';
import FormattingToolbar from '../FormattingToolbar';

const AnswerForm = ({ initialContent = '', onSubmit, onCancel, placeholder = "Write your answer...", isReply = false }) => {
  const [content, setContent] = useState(initialContent);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const contentRef = useRef(null);
  
  const validate = () => {
    if (!content.trim()) {
      setError('Answer content is required');
      return false;
    }
    
    if (content.trim().length < 20) {
      setError('Answer must be at least 20 characters');
      return false;
    }
    
    setError('');
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      await onSubmit(content);
      
      // Reset form if not editing
      if (!initialContent) {
        setContent('');
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <FormattingToolbar onFormat={setContent} textRef={contentRef} />
        <textarea
          ref={contentRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={isReply ? 3 : 6}
          placeholder={`${placeholder} You can use Markdown formatting.`}
          className={`block w-full rounded-b-md border-t-0 ${
            error 
              ? 'border-red-300 dark:border-red-500 focus:ring-red-500 focus:border-red-500' 
              : 'border-gray-300 dark:border-gray-500 focus:ring-red-500 focus:border-red-500'
          } shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
        />
        {error && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
      
      <div className="flex justify-end space-x-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <X className="mr-2 -ml-1 h-5 w-5" />
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
            submitting ? 'opacity-75 cursor-not-allowed' : ''
          }`}
        >
          {submitting ? (
            <>
              <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
              {initialContent ? 'Updating...' : (isReply ? 'Post Reply' : 'Post Answer')}
            </>
          ) : (
            initialContent ? 'Update' : (isReply ? 'Post Reply' : 'Post Answer')
          )}
        </button>
      </div>
      
      {!isReply && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          <p>Tips for a good answer:</p>
          <ul className="list-disc list-inside ml-2 mt-1">
            <li>Provide details and share your experience</li>
            <li>Be clear and concise</li>
            <li>Proofread your answer before posting</li>
          </ul>
        </div>
      )}
    </form>
  );
};

export default AnswerForm;